import React, { useContext, useEffect, useState } from "react";
import { Modal, Box, Typography, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import NeoButton from "./NeoButton";
import PackOpeningModal from "./PackOpeningModal";
import TokenRewardModal from "./TokenRewardModal";
import { doc, updateDoc, getDoc, setDoc, increment } from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import gameMenuBg from "../@shared/assets/game_menu.png";
import rewardIcon from "../@shared/assets/icons/reward_icon.png";
import tasksIcon from "../@shared/assets/icons/tasks_icon.png";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { Close } from "@mui/icons-material";
import { MainContext } from "../App";
import CardRewardModal from "./CardRewardModal";
import { checkAndCompleteQuests } from "../@shared/utils/Utils";

const dummyPoints = {
  damageDealtPoints: 100,
  eliminationPoints: 50,
  placementPoints: 300,
  playedCardPoints: 100,
  discardCardPoints: 50,
  tradeCardPoints: 75,
};

// Styles
const backdropStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
};

const tabRow = {
  flexDirection: "row",
  justifyContent: "space-evenly",
  marginTop: 5,
  marginBottom: 10,
  paddingHorizontal: 4,
};

const modalWrapperStyle = {
  width: "90vw",
  maxWidth: "600px",
  aspectRatio: 4 / 5,
  borderRadius: "16px",
  position: "relative",
};

const modalContentStyle = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: "5% 8% 12%",
  backgroundImage: `url(${gameMenuBg})`,
  backgroundSize: "cover",
  position: "relative",
};

const questRow = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  marginBottom: 16,
  backgroundColor: "#452098",
  padding: 12,
  borderRadius: 10,
};

const scrollAreaStyle = {
  marginTop: "10px",
  overflowY: "auto",
  flex: 1,
};

const rewardRowStyle = {
  marginBottom: "12px",
  backgroundColor: "#452098",
  padding: "12px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
};

export default function TownQuestsModal({
  visible,
  onClose,
  town = "Void City",
  availableQuests = [],
}) {
  const { userData } = useAuthStore();
  const { Theme } = useContext(MainContext);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("easy");

  const handleAssign = async (quest) => {
    try {
      const questRef = doc(db, "users", userData.id, "quests", quest.id);
      await setDoc(questRef, {
        ...quest,
        assignedAt: Date.now(),
        progress: 0,
        completed: false,
      });
    } catch (err) {
      console.error("❌ Failed to assign quest:", err);
    }
  };

  const renderQuest = (quest) => {
    const isAssigned = (userData?.quests || []).some(
      (uq) => uq.id === quest.id
    );

    return (
      <Box key={quest.id} style={questRow}>
        <Box
          style={{
            width: "calc(100% - 100px)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Typography color="#fff" fontWeight="700" fontSize={14} mb={2}>
            {quest.title}
          </Typography>
          <Typography color="#ccc" fontSize={12} mb={6}>
            {quest.description}
          </Typography>
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: 5,
              borderRadius: 5,
              backgroundColor: "#ffffff40",
            }}
          >
            <img
              src={require("../@shared/assets/icons/reward_icon.png")}
              style={{ width: 20, height: 20 }}
            />
            <Typography color="#fee399" fontSize={13}>
              {quest.reward.xp} XP
            </Typography>
            <img
              src={require("../@shared/assets/infift_token.png")}
              style={{ width: 20, height: 20, marginLeft: 5 }}
            />
            <Typography color="#fee399" fontSize={13}>
              {quest.reward.tokens}
            </Typography>
          </Box>
        </Box>
        <Box style={{ marginLeft: 12, width: 70 }}>
          {isAssigned ? (
            <Typography
              color="#0f0"
              style={{ textAlign: "center", fontWeight: "bold", marginTop: 10 }}
            >
              Assigned
            </Typography>
          ) : (
            <NeoButton
              text="GO!"
              width={100}
              color={Theme.palette.primary.main}
              onClick={() => handleAssign(quest)}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Modal open={visible} onClose={onClose} closeAfterTransition>
        <AnimatePresence>
          {visible && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={backdropStyle}
            >
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -50, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={modalWrapperStyle}
              >
                <Box sx={modalContentStyle}>
                  {/* Title */}
                  <Typography
                    color="#fff"
                    fontWeight="bold"
                    variant="h5"
                    align="center"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    My Quests
                  </Typography>
                  <Box style={{ ...tabRow, display: "flex" }}>
                    {["easy", "medium", "hard"].map((level) => (
                      <Box key={level} style={{ marginRight: 10, width: 90 }}>
                        <NeoButton
                          key={level}
                          text={level.charAt(0).toUpperCase() + level.slice(1)}
                          width={100}
                          height={30}
                          color={
                            activeTab === level
                              ? Theme.palette.primary.main
                              : Theme.palette.secondary.main
                          }
                          onClick={() => setActiveTab(level)}
                        />
                      </Box>
                    ))}
                  </Box>

                  <Box sx={scrollAreaStyle}>
                    {availableQuests
                      .filter((q) => q.difficulty === activeTab)
                      .map(renderQuest)}
                  </Box>

                  {/* Close Button */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-2%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <NeoButton
                      isIcon={true}
                      icon={<Close />}
                      color={Theme.palette.secondary.main}
                      onClick={onClose}
                    />
                  </Box>
                </Box>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>
    </>
  );
}
